import { useMemo } from "react";
import { PanelHeader } from "./PanelHeader";
import { useT } from "../hooks/useLang";
import type { MunicipalFloodCamera } from "../map/layers";
import type { FallbackTier } from "@yala/shared";

/**
 * CctvFloodBrief — the municipality's flood-infrastructure camera net.
 *
 * Every camera Yala runs watches something that matters during a flood: the
 * three drainage pumps (PA/PB/PC) and a flood shelter house (บ้านร่ม). Two of
 * the five sit at Tha Sap Bridge, roughly 200 m from the river gauges that
 * FloodWatchProfile already charts. That pairing is the reason this panel
 * exists — "the gauge is at 12.9 m" and "the riverside pump camera has been
 * dark for an hour" are far more actionable together than apart.
 *
 * Deliberately does NOT embed video. The upstream HLS session URL is signed,
 * short-lived, and intermittently serves no fragments; embedding it would make
 * a fragile third-party stream a load-bearing part of the flood panel. We show
 * presence, we link out for video.
 */

interface Props {
  cameras: MunicipalFloodCamera[];
  viewerUrl: string;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

const KIND_LABEL: Record<MunicipalFloodCamera["kind"], { en: string; th: string }> = {
  "water-pump": { en: "WATER PUMP", th: "สูบน้ำ" },
  "flood-shelter": { en: "FLOOD SHELTER", th: "บ้านร่ม" },
  unknown: { en: "UNCLASSIFIED", th: "ไม่ระบุ" },
};

export function CctvFloodBrief({ cameras, viewerUrl, ageMinutes, fallbackTier }: Props) {
  const t = useT();

  const summary = useMemo(() => {
    const pumps = cameras.filter((c) => c.kind === "water-pump");
    const shelters = cameras.filter((c) => c.kind === "flood-shelter");
    const dark = cameras.filter((c) => !c.isLive);
    const riverSide = cameras.filter((c) => c.side === "river");
    return { pumps, shelters, dark, riverSide };
  }, [cameras]);

  // Group by operational kind so the pumps read as a fleet, not five loose dots.
  const grouped = useMemo(() => {
    const order: MunicipalFloodCamera["kind"][] = ["water-pump", "flood-shelter", "unknown"];
    return order
      .map((kind) => ({ kind, items: cameras.filter((c) => c.kind === kind) }))
      .filter((g) => g.items.length > 0);
  }, [cameras]);

  if (cameras.length === 0) {
    return (
      <div className="cctv-flood-brief">
        <PanelHeader
          title={t("FLOOD INFRASTRUCTURE CAMERAS", "กล้องโครงสร้างพื้นฐานน้ำท่วม")}
          ageMinutes={ageMinutes}
          fallbackTier={fallbackTier}
          source="yala-cctv"
        />
        <p className="cctv-flood-empty">
          {fallbackTier == null
            ? t("Loading the municipal camera registry.", "กำลังโหลดรายชื่อกล้อง")
            : t(
                "Camera registry unavailable. The municipal viewer may be down.",
                "ไม่สามารถโหลดรายชื่อกล้อง ระบบดูภาพกล้องอาจไม่ทำงาน",
              )}
        </p>
      </div>
    );
  }

  return (
    <div className="cctv-flood-brief">
      <PanelHeader
        title={t("FLOOD INFRASTRUCTURE CAMERAS", "กล้องโครงสร้างพื้นฐานน้ำท่วม")}
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="yala-cctv"
        actions={
          <a
            className="cctv-flood-link mono"
            href={viewerUrl}
            target="_blank"
            rel="noreferrer"
          >
            {t("OPEN VIEWER", "เปิดระบบดูภาพ")}
          </a>
        }
      />

      <div className="cctv-flood-stats mono">
        <span>
          {summary.pumps.length} {t("PUMPS", "สูบน้ำ")}
        </span>
        <span className="cctv-flood-sep">·</span>
        <span>
          {summary.shelters.length} {t("SHELTER CAM", "กล้องบ้านร่ม")}
        </span>
        <span className="cctv-flood-sep">·</span>
        <span style={{ color: summary.dark.length > 0 ? "var(--st-warning-text)" : "var(--st-normal-text)" }}>
          {cameras.length - summary.dark.length}/{cameras.length} {t("ONLINE", "ออนไลน์")}
        </span>
      </div>

      {/* Only surfaced when it means something: a dark camera on a flood asset. */}
      {summary.dark.length > 0 && (
        <div className="cctv-flood-alert" role="alert">
          {t(
            `${summary.dark.length} camera${summary.dark.length > 1 ? "s" : ""} not reporting — check before relying on it.`,
            `กล้อง ${summary.dark.length} ตัวไม่มีสัญญาณ — ควรตรวจสอบก่อนใช้งาน`,
          )}
        </div>
      )}

      {grouped.map((group) => (
        <div key={group.kind} className="cctv-flood-group">
          <div className="eyebrow cctv-flood-group-head">
            {t(KIND_LABEL[group.kind].en, KIND_LABEL[group.kind].th)}
          </div>
          {group.items.map((c) => (
            <div key={c.id} className="cctv-flood-row">
              <span
                className={`cctv-flood-dot${c.isLive ? "" : " cctv-flood-dot--dark"}`}
                aria-hidden="true"
              />
              <span className="cctv-flood-name">{c.nameTh}</span>
              {c.side && (
                <span className="cctv-flood-side mono">
                  {c.side === "river"
                    ? t("RIVERSIDE", "ฝั่งแม่น้ำ")
                    : t("MUNICIPAL", "ฝั่งเทศบาล")}
                </span>
              )}
              <span className={`cctv-flood-state mono${c.isLive ? "" : " cctv-flood-state--dark"}`}>
                {c.isLive ? t("LIVE", "สด") : t("DARK", "ไม่มีสัญญาณ")}
              </span>
            </div>
          ))}
        </div>
      ))}

      <p className="cctv-flood-foot">
        {t(
          "Presence only — open the viewer for live video.",
          "ข้อมูลสถานะเท่านั้น — เปิดระบบดูภาพเพื่อดูสด",
        )}
      </p>
    </div>
  );
}
